import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/api";
import PropertyCard from "@/components/PropertyCard";
import SEO from "@/components/SEO";
import { SlidersHorizontal } from "lucide-react";

export default function Properties() {
  const [params, setParams] = useSearchParams();
  const [data, setData] = useState({ items: [], total: 0 });
  const [loading, setLoading] = useState(true);
  const [showFilters, setShowFilters] = useState(false);
  const [cities, setCities] = useState([]);
  const [localities, setLocalities] = useState([]);
  const [localityMode, setLocalityMode] = useState("select"); // "select" | "other"

  const getP = useCallback((k, def="") => params.get(k) || def, [params]);
  const setP = (k, v) => {
    const np = new URLSearchParams(params);
    if (v) np.set(k, v); else np.delete(k);
    // clear locality when city changes
    if (k === "city") np.delete("locality");
    setParams(np);
  };

  useEffect(() => {
    setLoading(true);
    // Only send area_unit filter when a min_area is actually set; otherwise it needlessly excludes properties in other units.
    const scrubbed = new URLSearchParams(params);
    if (!scrubbed.get("min_area")) scrubbed.delete("area_unit");
    const qs = scrubbed.toString();
    api.get(`/properties?${qs}`).then((r) => setData(r.data)).finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    api.get("/geo/cities").then((r) => setCities(r.data.items || []));
  }, []);

  useEffect(() => {
    const city = getP("city");
    const url = city ? `/geo/localities?city=${encodeURIComponent(city)}` : "/geo/localities";
    api.get(url).then((r) => setLocalities(r.data.items || []));
  }, [getP]);

  const verified = getP("verified_only") === "true";

  // Build a unique title + description per property-type filter (fixes duplicate meta SEO warning)
  const ptype = getP("property_type");
  const city = getP("city") || "Rewa";
  const seoCopy = {
    residential: { title: `Residential Properties for Sale in ${city}`, desc: `Buy verified residential homes, flats and villas in ${city}. Filter by BHK, budget, locality and legal status. RERA-approved listings on Tharlo.` },
    plot: { title: `Plots & Land for Sale in ${city}`, desc: `Explore verified residential and investment plots in ${city}. Clear-title land parcels with legal verification, road access details and fair-price ranges — Tharlo.` },
    commercial: { title: `Commercial Properties for Sale & Rent in ${city}`, desc: `Discover shops, showrooms, offices and warehouses in ${city}. Verified commercial spaces with locality, road-width and legal-status details on Tharlo.` },
    agricultural: { title: `Agricultural Land for Sale in ${city}`, desc: `Find fertile agricultural land parcels around ${city}. Filter by irrigation, road access and area. All listings legally verified by Tharlo.` },
  };
  const seo = seoCopy[ptype] || {
    title: `Properties for Sale in ${city}`,
    desc: `Browse verified homes, plots, commercial and agricultural properties in ${city}. Filter by locality, price and area. Trusted, transparent listings on Tharlo.`,
  };

  return (
    <div className="container-tharlo py-12" data-testid="properties-page">
      <SEO
        title={seo.title}
        description={seo.desc}
        canonical={`https://tharlo.in/properties${ptype ? `?property_type=${ptype}` : ""}`}
      />
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-8">
        <div>
          <div className="overline mb-2">Listings</div>
          <h1 className="font-display text-4xl lg:text-5xl text-ink tracking-tight">
            Properties {getP("city") ? `in ${getP("city")}` : "in Rewa"}
          </h1>
          <p className="text-muted-foreground mt-2 text-sm">{data.total} verified properties available</p>
        </div>
        <div className="flex items-center gap-3">
          <button onClick={() => setShowFilters(!showFilters)} data-testid="filters-toggle" className="md:hidden btn-outline !py-2.5">
            <SlidersHorizontal className="w-4 h-4" /> Filters
          </button>
          <select data-testid="sort-by" value={getP("sort_by", "latest")} onChange={(e) => setP("sort_by", e.target.value)} className="bg-white border border-border px-4 py-2.5 text-sm outline-none focus:border-ink">
            <option value="latest">Recommended</option>
            <option value="complete">Most Complete Listings</option>
            <option value="price_low">Price: Low to High</option>
            <option value="price_high">Price: High to Low</option>
            <option value="featured">Featured</option>
            <option value="verified">Verified First</option>
          </select>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
        {/* Filters */}
        <aside className={`lg:col-span-3 ${showFilters ? "block" : "hidden lg:block"}`}>
          <div className="bg-white border border-border p-6 sticky top-24 space-y-5 max-h-[calc(100vh-7rem)] overflow-y-auto">
            <div className="flex items-center justify-between">
              <h3 className="font-semibold text-ink">Filters</h3>
              <button onClick={() => { setParams(new URLSearchParams()); }} data-testid="filters-clear" className="text-xs text-gold hover:underline">Clear all</button>
            </div>

            <FilterBlock label="City">
              <select data-testid="filter-city" className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-ink bg-white" value={getP("city")} onChange={(e)=>setP("city", e.target.value)}>
                <option value="">All cities</option>
                {cities.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </FilterBlock>

            <FilterBlock label="Locality">
              {localityMode === "select" ? (
                <select
                  data-testid="filter-locality"
                  className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-ink bg-white"
                  value={localities.includes(getP("locality")) ? getP("locality") : ""}
                  onChange={(e) => {
                    if (e.target.value === "__other__") { setLocalityMode("other"); setP("locality", ""); }
                    else setP("locality", e.target.value);
                  }}
                >
                  <option value="">All localities</option>
                  {localities.map((l) => <option key={l} value={l}>{l}</option>)}
                  <option value="__other__">Other (specify)...</option>
                </select>
              ) : (
                <div className="flex gap-2">
                  <input
                    data-testid="filter-locality-text"
                    className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-ink"
                    placeholder="Type locality name..."
                    value={getP("locality")}
                    onChange={(e) => setP("locality", e.target.value)}
                    autoFocus
                  />
                  <button
                    type="button"
                    onClick={() => { setLocalityMode("select"); setP("locality", ""); }}
                    className="text-xs text-gold hover:underline whitespace-nowrap"
                  >
                    ← List
                  </button>
                </div>
              )}
            </FilterBlock>

            <FilterBlock label="Keyword">
              <input data-testid="filter-q" className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-ink" placeholder="Title, description..." value={getP("q")} onChange={(e)=>setP("q", e.target.value)} />
            </FilterBlock>

            <FilterBlock label="Property Type">
              <select data-testid="filter-type" className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-ink" value={getP("property_type")} onChange={(e)=>setP("property_type", e.target.value)}>
                <option value="">All</option>
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="plot">Plot</option>
                <option value="agricultural">Agricultural</option>
              </select>
            </FilterBlock>

            <FilterBlock label="Listing Type">
              <select data-testid="filter-listing" className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-ink" value={getP("listing_type")} onChange={(e)=>setP("listing_type", e.target.value)}>
                <option value="">Both</option>
                <option value="sale">For Sale</option>
                <option value="rent">For Rent</option>
              </select>
            </FilterBlock>

            <FilterBlock label="Min Price (₹)">
              <input data-testid="filter-min-price" type="number" className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-ink" placeholder="e.g. 2000000" value={getP("min_price")} onChange={(e)=>setP("min_price", e.target.value)} />
            </FilterBlock>
            <FilterBlock label="Max Price (₹)">
              <input data-testid="filter-max-price" type="number" className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-ink" placeholder="e.g. 10000000" value={getP("max_price")} onChange={(e)=>setP("max_price", e.target.value)} />
            </FilterBlock>

            <FilterBlock label="Min Area">
              <div className="grid grid-cols-[1fr_auto] gap-2">
                <input data-testid="filter-min-area" type="number" className="w-full border border-border px-3 py-2 text-sm outline-none focus:border-ink" placeholder="e.g. 1000" value={getP("min_area")} onChange={(e)=>setP("min_area", e.target.value)} />
                <select data-testid="filter-area-unit" className="border border-border px-2 py-2 text-sm outline-none focus:border-ink" value={getP("area_unit") || "sqft"} onChange={(e)=>setP("area_unit", e.target.value)}>
                  <option value="sqft">sqft</option>
                  <option value="sqyd">sqyd</option>
                  <option value="acre">acre</option>
                  <option value="bigha">bigha</option>
                </select>
              </div>
              <p className="text-[10px] text-muted-foreground mt-1">Filters to properties listed in the same unit.</p>
            </FilterBlock>

            <label className="flex items-center gap-2 text-sm cursor-pointer">
              <input data-testid="filter-verified" type="checkbox" checked={verified} onChange={(e)=>setP("verified_only", e.target.checked ? "true" : "")} className="accent-gold" />
              Verified Properties Only
            </label>
          </div>
        </aside>

        {/* Results */}
        <main className="lg:col-span-9">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {[...Array(6)].map((_,i)=>(<div key={`skel-${i}`} className="bg-white border border-border aspect-[4/5] animate-pulse" />))}
            </div>
          ) : data.items.length === 0 ? (
            <div className="bg-white border border-border p-16 text-center">
              <h3 className="font-display text-2xl text-ink mb-2">No properties found</h3>
              <p className="text-muted-foreground">Try adjusting your filters.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
              {data.items.map((p, i) => <PropertyCard key={p.id} p={p} index={i} />)}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

function FilterBlock({ label, children }) {
  return (
    <div>
      <div className="text-xs font-bold uppercase tracking-[0.15em] text-muted-foreground mb-2">{label}</div>
      {children}
    </div>
  );
}
